import React, { Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TrailerDialog from "./modals/trailers/TrailerDialog";
import EquipmentDialog from "./modals/equipment/EquipmentDialog";
import EquipmentTypeDialog from "./modals/equipment/EquipmentTypeDialog";
import EquipmentDepotDialog from "./modals/equipment/EquipmentDepotDialog";
import FractionDialog from "./modals/equipment/FractionDialog";
import TeamDialog from "./modals/TeamDialog";
import { lazyRetry } from "../helpers";
import { useAppDispatch } from "../redux-store";
import NewsModal from "./modals/NewsModal";
import EndRouteDialogPrompt from "./modals/EndRouteDialogPrompt";
import { CargoFormData } from "../ducks/app/cargo-dialog/types";
import * as cargoDialogActions from "../ducks/app/cargo-dialog";
import * as scheduleCargoTemplateDialogActions from "../ducks/app/schedule-cargo-template-dialog";
import * as clientCargoTemplateDialogActions from "../ducks/app/client-cargo-template-dialog";
import { RouteToArchiveSuggestion } from "dora-contracts";
import { useStableNavigate } from "./StableNavigateContext";
import AddEditCargoModalWrapper from "./modals/AddEditCargo";
import AddEditScheduleCargoTemplateModalWrapper from "./modals/AddEditScheduleCargoTemplate";
import AddEditClientCargoTemplateModalWrapper from "./modals/AddEditClientCargoTemplate";
import RequireAuth from "./RequireAuth";
import { useFeature } from "../hooks";

const LazyOrderDialog = React.lazy(() =>
  lazyRetry(() => import("./modals/OrderModal/OrderModal"), "order-modal")
);

const DriverDialog = React.lazy(() =>
  lazyRetry(() => import("./modals/drivers/DriverDialog"), "driver-dialog")
);

const OrderModalLoader = (props: { orderId: string; onClose: () => void }) => {
  return (
    <Suspense>
      <LazyOrderDialog {...props} />
    </Suspense>
  );
};

const CargoModal = () => {
  const { hash, search } = useLocation();
  const params = new URLSearchParams(hash.substring(1));
  const cargoId = params.get("cargo");
  const routeId = params.get("route");
  const navigate = useStableNavigate();
  const dispatch = useAppDispatch();
  const liteVersion = useFeature("lite-version");

  useEffect(() => {
    if (!cargoId) {
      return;
    }

    if (cargoId === "new") {
      if (routeId) {
        dispatch(cargoDialogActions.createNewCargo(liteVersion));
      } else {
        dispatch(cargoDialogActions.createNewCargo(liteVersion));
      }
    }
  }, [dispatch, cargoId, routeId, liteVersion]);

  const onClose = ({
    formData,
    cargoId,
  }: {
    formData?: CargoFormData;
    cargoId?: string;
  }) => {
    dispatch(cargoDialogActions.close(formData));
    navigate({ hash: "", search });
    if (cargoId) {
      dispatch(cargoDialogActions.loadRouteToArchiveSuggestion(cargoId))
        .unwrap()
        .then(setRouteToBeArchivedForPrompt)
        .catch(() => {
          // The thunk has already reported it; just skip the optional prompt.
        });
    }
  };

  const [routeToBeArchivedForPrompt, setRouteToBeArchivedForPrompt] =
    useState<RouteToArchiveSuggestion>(null);

  let shipmentEditModalComponent = null;
  if (cargoId) {
    if (cargoId === "new") {
      shipmentEditModalComponent = (
        <AddEditCargoModalWrapper
          onClose={onClose}
          routeId={routeId || undefined}
        />
      );
    } else {
      shipmentEditModalComponent = (
        <AddEditCargoModalWrapper onClose={onClose} cargoId={cargoId} />
      );
    }
  }

  return (
    <>
      {routeToBeArchivedForPrompt && (
        <EndRouteDialogPrompt
          route={routeToBeArchivedForPrompt}
          onClose={() => setRouteToBeArchivedForPrompt(null)}
        />
      )}
      {shipmentEditModalComponent}
    </>
  );
};

const ScheduleCargoTemplateModal = () => {
  const { hash } = useLocation();
  const params = new URLSearchParams(hash.substring(1));
  const cargoTemplateId = params.get("cargoTemplate");
  const routeTemplateId = params.get("routeTemplateId");
  const navigate = useStableNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!cargoTemplateId) {
      return;
    }

    if (cargoTemplateId === "new") {
      dispatch(scheduleCargoTemplateDialogActions.createNewCargoTemplate());
    }
  }, [dispatch, cargoTemplateId]);

  const onClose = () => {
    dispatch(scheduleCargoTemplateDialogActions.close(undefined));
    navigate({ hash: "" });
  };

  let shipmentTemplateEditModalComponent = null;
  if (cargoTemplateId) {
    if (cargoTemplateId === "new") {
      shipmentTemplateEditModalComponent = (
        <AddEditScheduleCargoTemplateModalWrapper
          routeTemplateId={routeTemplateId}
          onClose={onClose}
        />
      );
    } else {
      shipmentTemplateEditModalComponent = (
        <AddEditScheduleCargoTemplateModalWrapper
          routeTemplateId={routeTemplateId}
          cargoTemplateId={cargoTemplateId}
          onClose={onClose}
        />
      );
    }
  }

  return shipmentTemplateEditModalComponent;
};

const ClientCargoTemplateModal = () => {
  const { hash } = useLocation();
  const params = new URLSearchParams(hash.substring(1));
  const clientCargoTemplateId = params.get("clientCargoTemplate");
  const navigate = useStableNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!clientCargoTemplateId) {
      return;
    }

    if (clientCargoTemplateId === "new") {
      dispatch(clientCargoTemplateDialogActions.createNewCargoTemplate());
    }
  }, [dispatch, clientCargoTemplateId]);

  const onClose = () => {
    dispatch(clientCargoTemplateDialogActions.close(undefined));
    navigate({ hash: "" });
  };

  let shipmentTemplateEditModalComponent = null;
  if (clientCargoTemplateId) {
    if (clientCargoTemplateId === "new") {
      shipmentTemplateEditModalComponent = (
        <AddEditClientCargoTemplateModalWrapper onClose={onClose} />
      );
    } else {
      shipmentTemplateEditModalComponent = (
        <AddEditClientCargoTemplateModalWrapper
          cargoTemplateId={clientCargoTemplateId}
          onClose={onClose}
        />
      );
    }
  }

  return shipmentTemplateEditModalComponent;
};

const OrderModal = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  if (!hash || !hash.length) return null;
  const params = new URLSearchParams(hash.substring(1));
  const orderId = params.get("order");
  if (!orderId) return null;
  const closeModal = () => {
    navigate({ hash: "", search: window.location.search }, { replace: true });
  };
  return <OrderModalLoader orderId={orderId} onClose={closeModal} />;
};

const Dialogs = () => (
  <>
    <RequireAuth>
      <Suspense fallback={null}>
        <DriverDialog />
        <CargoModal />
        <ScheduleCargoTemplateModal />
        <ClientCargoTemplateModal />
        <TrailerDialog />
        <EquipmentDialog />
        <EquipmentTypeDialog />
        <EquipmentDepotDialog />
        <FractionDialog />
        <TeamDialog />
        <OrderModal />
      </Suspense>
    </RequireAuth>
    <NewsModal />
  </>
);

export default Dialogs;
